// src/component/AnnouncementAdminPage.js
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Checkbox,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  updateDoc,
  query,
  orderBy,
} from "firebase/firestore";

import { db, auth } from "../api/firebase";
import { AppThemeContext } from "./AppShell";
import PostContent from "./PostContent";
import { useNavigate } from "react-router-dom";

// ───────────────────────────────────
// 期・セカイムラ設定用
// ───────────────────────────────────
const PAYMENT_TERMS = [1, 2, 3, 4];
const ALLOWED_SEKAIMURA_KEY = "sekaiMura";

const normalizeTerms = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((t) => String(t).trim())
    .filter(Boolean);

const truncate = (text, max = 140) =>
  text && text.length > max ? text.slice(0, max) + "…" : text || "";

const formatDateTime = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : ts;
  return d.toLocaleString("ja-JP");
};

const formatDate = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : ts;
  return d.toLocaleDateString("ja-JP");
};

export default function AnnouncementAdminPage() {
  const theme = useContext(AppThemeContext) || {};
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const accent = theme.railActive ?? "#5A35FF";

  const navigate = useNavigate();

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isAdmin, setIsAdmin] = useState(false);

  // 新規作成用
  const [createOpen, setCreateOpen] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    content: "",
    isVisible: true,
    allowedTerm: [], // ★ 追加
  });

  // 編集用
  const [editOpen, setEditOpen] = useState(false);
  const [editAnnouncement, setEditAnnouncement] = useState(null);

  // 詳細表示用
  const [viewOpen, setViewOpen] = useState(false);
  const [viewAnnouncement, setViewAnnouncement] = useState(null);

  // 削除確認用
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  /* ==== 権限チェック & 一覧取得 ==== */
  useEffect(() => {
    const init = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};
        const adminFlag = !!userData.isAdmin;
        setIsAdmin(adminFlag);

        await fetchAnnouncements();
      } catch (e) {
        console.error("Failed to init announcement admin:", e);
        setLoading(false);
      }
    };

    const fetchAnnouncements = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, "announcements"),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setAnnouncements(list);
      } catch (e) {
        console.error("Failed to load announcements:", e);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  /* ==== ハンドラ ===== */

  const handleOpenCreate = () => {
    setNewAnnouncement({
      title: "",
      content: "",
      isVisible: true,
      allowedTerm: [], // 初期は何も選ばない（= 全体向けとして扱う想定）
    });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim())
      return;

    try {
      const colRef = collection(db, "announcements");
      const now = new Date();
      const allowedTerm = normalizeTerms(newAnnouncement.allowedTerm);

      const docRef = await addDoc(colRef, {
        title: newAnnouncement.title.trim(),
        content: newAnnouncement.content,
        isVisible: newAnnouncement.isVisible,
        allowedTerm, // ★ 保存
        createdAt: now,
      });

      setAnnouncements((prev) => [
        {
          id: docRef.id,
          ...newAnnouncement,
          allowedTerm,
          createdAt: now,
        },
        ...prev,
      ]);

      setCreateOpen(false);
    } catch (e) {
      console.error("Failed to add announcement:", e);
    }
  };

  const handleOpenEdit = (announcement) => {
    setEditAnnouncement({ ...announcement });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editAnnouncement) return;
    if (
      !editAnnouncement.title.trim() ||
      !editAnnouncement.content.trim()
    )
      return;

    try {
      const ref = doc(db, "announcements", editAnnouncement.id);
      const allowedTerm = normalizeTerms(editAnnouncement.allowedTerm);

      await updateDoc(ref, {
        title: editAnnouncement.title.trim(),
        content: editAnnouncement.content,
        allowedTerm, // ★ 更新
      });

      setAnnouncements((prev) =>
        prev.map((a) =>
          a.id === editAnnouncement.id
            ? { ...a, ...editAnnouncement, allowedTerm }
            : a
        )
      );

      setEditOpen(false);
      setEditAnnouncement(null);
    } catch (e) {
      console.error("Failed to update announcement:", e);
    }
  };

  const handleVisibilityToggle = async (announcement) => {
    try {
      const ref = doc(db, "announcements", announcement.id);
      await updateDoc(ref, { isVisible: !announcement.isVisible });

      setAnnouncements((prev) =>
        prev.map((a) =>
          a.id === announcement.id
            ? { ...a, isVisible: !announcement.isVisible }
            : a
        )
      );
    } catch (e) {
      console.error("Failed to toggle visibility:", e);
    }
  };

  const handleOpenView = (announcement) => {
    setViewAnnouncement(announcement);
    setViewOpen(true);
  };

  const handleOpenDelete = (announcement) => {
    setDeleteTarget(announcement);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, "announcements", deleteTarget.id));
      setAnnouncements((prev) =>
        prev.filter((a) => a.id !== deleteTarget.id)
      );
    } catch (e) {
      console.error("Failed to delete announcement:", e);
    } finally {
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  // ★ 一覧カード用：allowedTerm のトグル
  const handleToggleTerm = async (announcement, termValue) => {
    const termStr = String(termValue);
    const current = normalizeTerms(announcement.allowedTerm);
    const has = current.includes(termStr);
    const next = has
      ? current.filter((t) => t !== termStr)
      : [...current, termStr];

    try {
      const ref = doc(db, "announcements", announcement.id);
      await updateDoc(ref, { allowedTerm: next });

      setAnnouncements((prev) =>
        prev.map((a) =>
          a.id === announcement.id ? { ...a, allowedTerm: next } : a
        )
      );
    } catch (e) {
      console.error("Failed to update allowedTerm:", e);
    }
  };

  // ★ 新規作成ダイアログ用
  const handleNewToggleTerm = (termValue) => {
    const termStr = String(termValue);
    setNewAnnouncement((prev) => {
      const current = normalizeTerms(prev.allowedTerm);
      const has = current.includes(termStr);
      const next = has
        ? current.filter((t) => t !== termStr)
        : [...current, termStr];
      return { ...prev, allowedTerm: next };
    });
  };

  // ★ 編集ダイアログ用
  const handleEditToggleTerm = (termValue) => {
    const termStr = String(termValue);
    setEditAnnouncement((prev) => {
      if (!prev) return prev;
      const current = normalizeTerms(prev.allowedTerm);
      const has = current.includes(termStr);
      const next = has
        ? current.filter((t) => t !== termStr)
        : [...current, termStr];
      return { ...prev, allowedTerm: next };
    });
  };

  /* ==== UI ==== */

  return (
    <Box
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        pb: { xs: 3, sm: 4 },
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: "0.18em",
              fontSize: 11,
              opacity: 0.8,
              color: textBase,
            }}
          >
            ADMIN / ANNOUNCEMENTS
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: "0.03em",
              color: textBase,
            }}
          >
            基本情報管理
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}
          >
            事務局からの基本情報の登録・編集・表示／非表示と、
            期・セカイムラ単位での公開範囲を管理します。
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/admin")}
          >
            管理者メニューに戻る
          </Button>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              新しいお知らせ
            </Button>
          )}
        </Stack>
      </Box>

      {!isAdmin && (
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: "rgba(200,0,0,0.8)",
          }}
        >
          管理者権限がありません。閲覧のみ可能です。
        </Typography>
      )}

      {/* 一覧 */}
      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : announcements.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: "rgba(0,0,0,0.6)", mt: 1.5 }}
        >
          登録されているお知らせはありません。
        </Typography>
      ) : (
        <Stack spacing={1.8}>
          {announcements.map((a) => {
            const allowedSet = new Set(normalizeTerms(a.allowedTerm));
            return (
              <Card
                key={a.id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
                }}
              >
                {/* ★ カード全体クリックで詳細ダイアログ */}
                <CardActionArea
                  onClick={() => handleOpenView(a)}
                  sx={{ borderRadius: 3 }}
                >
                  <CardContent
                    sx={{
                      p: 2,
                      "&:last-child": { pb: 2 },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                    >
                      {/* 左：本文 */}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(0,0,0,0.55)",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          {a.createdAt
                            ? formatDate(a.createdAt)
                            : "日付未設定"}{" "}
                          /{" "}
                          {a.createdAt ? formatDateTime(a.createdAt) : ""}
                        </Typography>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: textBase,
                            mb: 0.4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {a.title || "（タイトル未設定）"}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: 13,
                            color: "rgba(0,0,0,0.7)",
                            lineHeight: 1.6,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            wordBreak: "break-word",
                          }}
                        >
                          {truncate(a.content || "")}
                        </Typography>

                        {/* ★ 期・セカイムラのチェック群（行クリックと分離） */}
                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {PAYMENT_TERMS.map((term) => (
                            <FormControlLabel
                              key={term}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={allowedSet.has(String(term))}
                                  disabled={!isAdmin}
                                  onChange={() =>
                                    handleToggleTerm(a, term)
                                  }
                                />
                              }
                              label={`${term}期`}
                              sx={{
                                m: 0,
                                mr: 1.5,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: 12,
                                },
                              }}
                            />
                          ))}

                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={allowedSet.has(
                                  ALLOWED_SEKAIMURA_KEY
                                )}
                                disabled={!isAdmin}
                                onChange={() =>
                                  handleToggleTerm(
                                    a,
                                    ALLOWED_SEKAIMURA_KEY
                                  )
                                }
                              />
                            }
                            label="セカイムラ民"
                            sx={{
                              m: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: 12,
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      {/* 右：操作系 */}
                      <Stack
                        spacing={1}
                        alignItems="flex-end"
                        sx={{ flexShrink: 0 }}
                      >
                        {/* 表示/非表示トグル（クリック伝播止める） */}
                        <FormControlLabel
                          onClick={(e) => e.stopPropagation()}
                          control={
                            <Switch
                              size="small"
                              checked={!!a.isVisible}
                              disabled={!isAdmin}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleVisibilityToggle(a);
                              }}
                            />
                          }
                          label={
                            a.isVisible ? (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption">
                                  表示中
                                </Typography>
                              </Stack>
                            ) : (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <VisibilityOffIcon
                                  sx={{ fontSize: 16 }}
                                />
                                <Typography variant="caption">
                                  非表示
                                </Typography>
                              </Stack>
                            )
                          }
                          sx={{
                            m: 0,
                            "& .MuiFormControlLabel-label": {
                              fontSize: 11,
                              color: "rgba(0,0,0,0.7)",
                            },
                          }}
                        />

                        <Box>
                          {/* 編集・削除ボタンもクリック伝播止める */}
                          {isAdmin && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenEdit(a);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDelete(a);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </Stack>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* ▼ 新規作成ダイアログ */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>新しいお知らせを追加</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={newAnnouncement.isVisible}
                onChange={(e) =>
                  setNewAnnouncement((prev) => ({
                    ...prev,
                    isVisible: e.target.checked,
                  }))
                }
              />
            }
            label="表示する"
          />

          {/* ★ 期・セカイムラ選択 */}
          <Box sx={{ mt: 1, mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 0.5 }}
            >
              表示対象の期・セカイムラ（未選択の場合は全体向けとして扱う想定）
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {PAYMENT_TERMS.map((term) => {
                const set = new Set(
                  normalizeTerms(newAnnouncement.allowedTerm)
                );
                return (
                  <FormControlLabel
                    key={term}
                    control={
                      <Checkbox
                        size="small"
                        checked={set.has(String(term))}
                        onChange={() => handleNewToggleTerm(term)}
                      />
                    }
                    label={`${term}期`}
                    sx={{
                      m: 0,
                      mr: 1.5,
                      "& .MuiFormControlLabel-label": { fontSize: 12 },
                    }}
                  />
                );
              })}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={normalizeTerms(
                      newAnnouncement.allowedTerm
                    ).includes(ALLOWED_SEKAIMURA_KEY)}
                    onChange={() =>
                      handleNewToggleTerm(ALLOWED_SEKAIMURA_KEY)
                    }
                  />
                }
                label="セカイムラ民"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: 12 },
                }}
              />
            </Box>
          </Box>

          <TextField
            autoFocus
            margin="dense"
            label="タイトル"
            fullWidth
            value={newAnnouncement.title}
            onChange={(e) =>
              setNewAnnouncement((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
          />
          <TextField
            margin="dense"
            label="内容"
            fullWidth
            multiline
            rows={8}
            value={newAnnouncement.content}
            onChange={(e) =>
              setNewAnnouncement((prev) => ({
                ...prev,
                content: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={
              !newAnnouncement.title.trim() ||
              !newAnnouncement.content.trim()
            }
          >
            追加
          </Button>
        </DialogActions>
      </Dialog>

      {/* ▼ 編集ダイアログ */}
      <Dialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditAnnouncement(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>お知らせを編集</DialogTitle>
        <DialogContent>
          {/* 期・セカイムラ選択（編集） */}
          <Box sx={{ mt: 0.5, mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 0.5 }}
            >
              表示対象の期・セカイムラ
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {PAYMENT_TERMS.map((term) => {
                const set = new Set(
                  normalizeTerms(editAnnouncement?.allowedTerm)
                );
                return (
                  <FormControlLabel
                    key={term}
                    control={
                      <Checkbox
                        size="small"
                        checked={set.has(String(term))}
                        onChange={() => handleEditToggleTerm(term)}
                      />
                    }
                    label={`${term}期`}
                    sx={{
                      m: 0,
                      mr: 1.5,
                      "& .MuiFormControlLabel-label": { fontSize: 12 },
                    }}
                  />
                );
              })}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={normalizeTerms(
                      editAnnouncement?.allowedTerm
                    ).includes(ALLOWED_SEKAIMURA_KEY)}
                    onChange={() =>
                      handleEditToggleTerm(ALLOWED_SEKAIMURA_KEY)
                    }
                  />
                }
                label="セカイムラ民"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: 12 },
                }}
              />
            </Box>
          </Box>

          <TextField
            autoFocus
            margin="dense"
            label="タイトル"
            fullWidth
            value={editAnnouncement?.title || ""}
            onChange={(e) =>
              setEditAnnouncement((prev) =>
                prev ? { ...prev, title: e.target.value } : prev
              )
            }
          />
          <TextField
            margin="dense"
            label="内容"
            fullWidth
            multiline
            rows={8}
            value={editAnnouncement?.content || ""}
            onChange={(e) =>
              setEditAnnouncement((prev) =>
                prev ? { ...prev, content: e.target.value } : prev
              )
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditOpen(false);
              setEditAnnouncement(null);
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={
              !editAnnouncement?.title?.trim() ||
              !editAnnouncement?.content?.trim()
            }
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* ▼ 詳細表示ダイアログ */}
      <Dialog
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewAnnouncement(null);
        }}
        fullWidth
        maxWidth="md"
      >
        {viewAnnouncement && (
          <>
            <DialogTitle>
              {viewAnnouncement.title || "事務局からのお知らせ"}
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5 }}
              >
                {viewAnnouncement.createdAt
                  ? formatDateTime(viewAnnouncement.createdAt)
                  : ""}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <PostContent content={viewAnnouncement.content || ""} />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setViewOpen(false);
                  setViewAnnouncement(null);
                }}
              >
                閉じる
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ▼ 削除確認ダイアログ */}
      <Dialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
      >
        <DialogTitle>お知らせの削除</DialogTitle>
        <DialogContent>
          <Typography>
            本当にこのお知らせを削除しますか？ この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteOpen(false);
              setDeleteTarget(null);
            }}
          >
            キャンセル
          </Button>
          <Button color="error" onClick={handleDelete}>
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
